import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../../../../../auth-context";
import styled from "styled-components";
import moment from "moment";
import iconCheck from "../../../../../images/iconCheck.png";
import { getPortalSurveyResponse } from "../../../../../api";
import Icon from "@material-ui/core/Icon";
import { PreviewSurveyPDF } from "../../inspect/components/survey-pdf";
import Modal from "../../../../../components/modal";

const SurveyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0px 0px 20px 0px;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 auto 10px auto;
`;

const Date = styled.p`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 auto 10px auto;
  width: 190px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 1rem;
  width: 100%;
  text-align: center;
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 20px 0px;
`;

const QuestionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
`;

const QuestionDescription = styled.p`
  color: #555;
  font-size: 0.9rem !important;
`;

const Checkmark = styled.img`
  width: 26px;
  height: 26px;
  margin: 0 !important;
`;

const Circle = styled.div`
  border: 1px solid #555;
  width: 25px;
  height: 25px;
  border-radius: 50%;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  gap: 10px;
  margin-bottom: 3px;
  p {
    margin: 0 !important;
  }
`;

const DateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DateButton = styled.button`
  border: none;
  background: none;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  padding: 0;
  font-size: 1.75rem;
  opacity: ${(props) => (props.disabled ? 0 : 1)};
`;

const SurveyResponse = ({ data }) => {
  const { survey_data, response_data } = data;
  const renderResponse = (question, response) => {
    switch (question.responseType) {
      case "text":
        return <p>{response.response}</p>;
      case "multiple_choice":
      case "checkbox":
        const options = question?.options?.map((option) => {
          const isChecked = response?.response.includes(option.id);
          return (
            <Option key={option.id}>
              {isChecked ? (
                <Checkmark src={iconCheck} alt="check" />
              ) : (
                <Circle />
              )}
              <p>{option.value}</p>
            </Option>
          );
        });
        return options;
      case "linear_scale":
        return <p>{response.response}</p>;
      case "select_check_in":
        return <p>{response.response}</p>;
      default:
        return null;
    }
  };

  return (
    <SurveyContainer>
      <Title>{survey_data?.title}</Title>
      <Description>{survey_data?.description}</Description>
      {survey_data?.questions?.map((question) => {
        const response = response_data.find(
          (r) => r.question_id === question.id
        );
        return (
          <QuestionContainer key={question.id}>
            <QuestionTitle>{question.title}</QuestionTitle>
            <QuestionDescription>{question.description}</QuestionDescription>
            {response ? (
              renderResponse(question, response)
            ) : (
              <p style={{ fontStyle: "italic" }}>No response</p>
            )}
          </QuestionContainer>
        );
      })}
    </SurveyContainer>
  );
};

export const ClientSurveyModal = ({ open, onClose, clientUserID }) => {
  const { user } = useContext(AuthContext);
  const [responses, setResponses] = useState([]);
  const [selectedResponseIndex, setSelectedResponseIndex] = useState(0);
  const [openPDF, setOpenPDF] = useState(false);

  useEffect(() => {
    if (open) {
      getPortalSurveyResponse(user.id, clientUserID)
        .then((res) => {
          setResponses(res?.data?.responses);
          setSelectedResponseIndex(res?.data?.responses.length - 1);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [open, user.id, clientUserID]);

  const handlePrevious = () => {
    if (selectedResponseIndex > 0) {
      setSelectedResponseIndex(selectedResponseIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedResponseIndex < responses.length - 1) {
      setSelectedResponseIndex(selectedResponseIndex + 1);
    }
  };

  const showNextButton = selectedResponseIndex < responses.length - 1;
  const showPreviousButton = selectedResponseIndex > 0;

  return (
    <>
      <Modal
        open={open}
        onClose={() => onClose()}
        handleSubmit={() => setOpenPDF(true)}
        submitLabel="Export as PDF"
      >
        {responses.length > 0 && (
          <>
            <DateContainer>
              <DateButton
                onClick={showPreviousButton && handlePrevious}
                disabled={!showPreviousButton}
              >
                <Icon>chevron_left</Icon>
              </DateButton>
              <Date>
                {moment
                  .utc(responses[selectedResponseIndex].created_at)
                  .format("MMMM D, YYYY")}
              </Date>
              <DateButton
                onClick={showNextButton && handleNext}
                disabled={!showNextButton}
              >
                <Icon>chevron_right</Icon>
              </DateButton>
            </DateContainer>

            <SurveyResponse
              response={responses[selectedResponseIndex]}
              data={responses[selectedResponseIndex]}
            />
          </>
        )}
      </Modal>
      <PreviewSurveyPDF
        open={openPDF}
        handleClose={() => setOpenPDF(false)}
        data={responses[selectedResponseIndex]}
      />
    </>
  );
};
